import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLine } from "../Reveal";
import { PLATES } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { ArrowDownRight } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  // A slower rate on the overlapping plate, so the two separate as you scroll
  // instead of moving as one flat block.
  const yPlate = useTransform(scrollYProgress, [0, 1], [0, 20]);

  const lines = t.hero.titleLines;

  return (
    <section ref={ref} data-testid="hero-section" className="relative pt-40 md:pt-52 pb-20 overflow-hidden">
      {/* Survey-line texture, borrowed from maker/atelier sites that print their
          coordinates on the label — a single static vector, not a tiled raster,
          so it stays crisp at any size and costs one paint, never a repaint. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1400 900"
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-0 h-full w-full text-forest opacity-[0.05]"
      >
        <path d="M-50 120 C 250 40, 500 210, 780 110 S 1250 60, 1450 160" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M-50 260 C 300 190, 560 340, 860 250 S 1220 180, 1450 300" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M-50 620 C 280 700, 540 560, 830 660 S 1240 740, 1450 630" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M-50 780 C 320 850, 600 720, 900 800 S 1260 880, 1450 770" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M980 -20 C 1040 180, 900 320, 1080 460 S 1000 700, 1120 920" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="dim-line text-forest" />
          <span className="micro text-forest/70">
            {t.hero.badge}
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-display text-[13vw] leading-[0.92] md:text-[7.5rem] lg:text-[8.5rem] tracking-tight text-charcoal">
              {lines.map((line, i) => (
                <MaskLine
                  key={`${lang}-${i}`}
                  delay={0.1 + i * 0.12}
                  className={i === 1 ? "text-forest" : i === lines.length - 1 ? "text-forest" : ""}
                >
                  {line}
                </MaskLine>
              ))}
            </h1>
          </div>

          {/* Two plates on different parallax rates, the lower one breaking out
              of the column and overlapping the first. The offset is what makes
              them read as layered onto the page rather than dropped into a
              slot. Eager on the wide plate — it's above the fold, so lazy
              loading would just delay the largest thing in the viewport. */}
          <motion.div style={{ y: yImg }} className="lg:col-span-4 hidden lg:block relative">
            <div className="crop-frame">
              <div className="relative overflow-hidden rounded-sm">
                <img
                  src={PLATES.wide}
                  alt="Desert at dusk, photographed by the studio"
                  decoding="async"
                  className="duotone-plate w-full aspect-[4/5] object-cover"
                />
                <div aria-hidden="true" className="duotone-wash" />
              </div>
            </div>
            {/* -bottom-20 measured fine at 1280 but overlapped the CTA row by
                8px at 1024 — the wide plate above it shrinks with the column
                at lg, so a fixed px offset gets proportionally larger as the
                viewport narrows. Smaller offset plus a smaller max parallax
                range on yPlate keeps clearance at the narrowest width this
                column actually renders at. */}
            <motion.div
              style={{ y: yPlate }}
              className="absolute -bottom-4 -left-10 w-[52%] overflow-hidden rounded-sm border-4 border-cream shadow-[0_30px_60px_-25px_rgba(28,25,23,0.5)]"
            >
              <img
                src={PLATES.tall}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className="w-full aspect-[3/4] object-cover"
              />
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.7 }}
          className="mt-12 grid md:grid-cols-2 gap-10 items-end"
        >
          <p className="max-w-xl text-lg md:text-xl leading-relaxed text-charcoal/70">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-4 md:justify-end">
            <a
              href="#portfolio"
              data-testid="hero-explore-btn"
              className="group btn-block bg-forest text-cream hover:bg-charcoal"
            >
              {t.hero.explore}
              <ArrowDownRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform duration-300" />
            </a>
            <a
              href="#contact"
              data-testid="hero-quote-btn"
              className="btn-block border border-forest/30 text-forest hover:border-forest hover:bg-forest/5"
            >
              {t.hero.quote}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
